import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cn } from '@/helpers/cn';
import { useEvent } from '@/hooks/useEvent';
import { useIsTouchDevice } from '@/hooks/useIsTouchDevice';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { FocusScope, useFocusManager } from 'react-aria';
import { createPortal } from 'react-dom';
const TooltipContent = ({ tooltipRef, tooltipId, testid, handleMouseEnter, handleMouseLeave, position, content, shouldFocus, }) => {
    const focusManager = useFocusManager();
    useEffect(() => {
        if (shouldFocus && tooltipRef.current && focusManager) {
            focusManager.focusFirst();
        }
    }, [shouldFocus, focusManager]);
    return (_jsx("div", { ref: tooltipRef, id: tooltipId, role: "tooltip", "aria-live": "polite", tabIndex: -1, "data-testid": `${testid}-content`, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, style: {
            '--top': `${position.top}px`,
            '--left': `${position.left}px`,
        }, className: "fixed top-(--top) left-(--left) z-50 max-w-15 rounded-md bg-surface-primary p-1.5 break-words shadow-sm sm:max-w-20 dark:focus-visible:outline-border-contrast light-a:focus-visible:outline-border-primary light-b:focus-visible:outline-border-expressive", children: content }));
};
const Tooltip = ({ children, content, className, testid = 'tooltip', ariaLabel = 'Info', closeOnClick = false, hideDelay = 0, }) => {
    const [visible, setVisible] = useState(false);
    const [shouldFocus, setShouldFocus] = useState(false);
    const tooltipRef = useRef(null);
    const triggerRef = useRef(null);
    const buttonRef = useRef(null);
    const isTouchDevice = useIsTouchDevice();
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const tooltipId = useId();
    const hideTimeoutRef = useRef(null);
    const clearHideTimeout = () => {
        if (hideTimeoutRef.current !== null) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }
    };
    const scheduleHide = () => {
        if (isTouchDevice || closeOnClick)
            return;
        clearHideTimeout();
        hideTimeoutRef.current = window.setTimeout(() => {
            setVisible(false);
            hideTimeoutRef.current = null;
        }, hideDelay);
    };
    useEvent('keydown', (event) => {
        if (event.key === 'Escape' && visible) {
            setVisible(false);
        }
    });
    useLayoutEffect(() => {
        if (visible && tooltipRef.current && triggerRef.current) {
            const tooltipRect = tooltipRef.current.getBoundingClientRect();
            const triggerRect = triggerRef.current.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            let newLeft = triggerRect.left;
            let newTop = triggerRect.top - tooltipRect.height - 8;
            if (newTop < 0) {
                newTop = triggerRect.bottom + 8;
            }
            if (newLeft + tooltipRect.width > viewportWidth) {
                newLeft = triggerRect.right - tooltipRect.width - 8;
            }
            if (newLeft + tooltipRect.width > viewportWidth) {
                newLeft = viewportWidth - tooltipRect.width - 8;
            }
            if (newTop + tooltipRect.height > viewportHeight) {
                newTop = viewportHeight - tooltipRect.height - 8;
            }
            if (newLeft < 8) {
                newLeft = 24;
            }
            setPosition({ top: newTop, left: newLeft });
            setShouldFocus(true);
        }
        else {
            setShouldFocus(false);
        }
    }, [visible]);
    useEffect(() => {
        return () => {
            clearHideTimeout();
        };
    }, []);
    useEffect(() => {
        const controller = new AbortController();
        const { signal } = controller;
        document.addEventListener('mousedown', (event) => {
            if (tooltipRef.current &&
                !tooltipRef.current.contains(event.target) &&
                triggerRef.current &&
                !triggerRef.current.contains(event.target)) {
                setVisible(false);
            }
        }, { signal });
        return () => controller.abort();
    }, [visible]);
    const handleMouseEnter = () => {
        if (!isTouchDevice && !closeOnClick) {
            clearHideTimeout();
            setVisible(true);
        }
    };
    const handleMouseLeave = () => {
        if (!isTouchDevice && !closeOnClick) {
            scheduleHide();
        }
    };
    useEffect(() => {
        const handleScroll = () => {
            if (visible) {
                setVisible(false);
            }
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleScroll);
        };
    }, [visible]);
    const handleTriggerClick = () => {
        // We need this verification because there is a bug on Windows that is not accept the keydown event
        const isFocused = document.activeElement === buttonRef.current;
        if (isTouchDevice || closeOnClick || isFocused) {
            setVisible((prev) => !prev);
        }
    };
    return (_jsxs("div", { className: cn('relative flex items-center', className), "data-testid": testid, ref: triggerRef, children: [_jsx("button", { ref: buttonRef, onClick: handleTriggerClick, "aria-label": ariaLabel, "aria-describedby": visible ? tooltipId : undefined, "aria-expanded": visible, onKeyDown: (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setVisible((prev) => !prev);
                    }
                }, children: children }), visible &&
                createPortal(_jsx(FocusScope, { autoFocus: true, restoreFocus: true, children: _jsx(TooltipContent, { tooltipRef: tooltipRef, tooltipId: tooltipId, testid: testid, handleMouseEnter: handleMouseEnter, handleMouseLeave: handleMouseLeave, position: position, content: content, shouldFocus: shouldFocus }) }), document.body)] }));
};
export default Tooltip;
